import {
  ambientWorldCatalog,
  ambientWorldCompositionBudget,
  type AmbientWorldEventKind,
  type AmbientWorldEventPolicy,
} from './fishing-world-catalog'
import type { FishingWorldActiveEvent } from './fishing-world-scheduler'
import { validateMicroLifePlacementForKind } from './fishing-world-anchors'
import { fishingMicroLifePresentationByEventKind } from './fishing-world-sprite-manifest'

export type FishingWorldRenderCatalog = Readonly<Record<AmbientWorldEventKind, AmbientWorldEventPolicy>>

export type FishingWorldRenderDecision = Readonly<{
  activeEvents: readonly FishingWorldActiveEvent[]
}>

export const FishingWorldAmbientAdmissionRejection = {
  EventAlreadyActive: 'event-already-active',
  MajorActive: 'major-active',
  MinorCompositionLimit: 'minor-composition-limit',
  MinorVisualBudget: 'minor-visual-budget',
} as const

export type FishingWorldAmbientAdmissionRejection = typeof FishingWorldAmbientAdmissionRejection[keyof typeof FishingWorldAmbientAdmissionRejection]

export function fishingWorldAmbientAdmissionRejection(
  activeEvents: readonly FishingWorldActiveEvent[],
  candidateKind: AmbientWorldEventKind,
  catalog: FishingWorldRenderCatalog = ambientWorldCatalog,
): FishingWorldAmbientAdmissionRejection | null {
  const candidate = catalog[candidateKind]
  if (activeEvents.some((event) => event.kind === candidateKind)) return FishingWorldAmbientAdmissionRejection.EventAlreadyActive
  if (candidate.tier === 'major') {
    return activeEvents.some((event) => catalog[event.kind].tier === 'major')
      ? FishingWorldAmbientAdmissionRejection.MajorActive
      : null
  }
  const activeMinors = activeEvents.filter((event) => catalog[event.kind].tier === 'minor')
  if (activeMinors.length >= ambientWorldCompositionBudget.maximumActiveMinors) return FishingWorldAmbientAdmissionRejection.MinorCompositionLimit
  const minorVisualCost = activeMinors.reduce((total, event) => total + catalog[event.kind].visualCost, 0)
  return minorVisualCost + candidate.visualCost > ambientWorldCompositionBudget.maximumMinorVisualCost
    ? FishingWorldAmbientAdmissionRejection.MinorVisualBudget
    : null
}

/** Shared admission guard for both runtime forcing and the render boundary. */
export function canAddFishingWorldAmbientEvent(
  activeEvents: readonly FishingWorldActiveEvent[],
  candidateKind: AmbientWorldEventKind,
  catalog: FishingWorldRenderCatalog = ambientWorldCatalog,
) {
  return fishingWorldAmbientAdmissionRejection(activeEvents, candidateKind, catalog) === null
}

/**
 * The renderer boundary mirrors scheduler limits as a defensive last step.
 * It keeps the renderer bounded even during a React state transition.
 */
export function createFishingWorldRenderDecision(
  activeEvents: readonly FishingWorldActiveEvent[],
  catalog: FishingWorldRenderCatalog = ambientWorldCatalog,
): FishingWorldRenderDecision {
  const selected: FishingWorldActiveEvent[] = []
  for (const event of [...activeEvents].sort((left, right) => left.startedAtMs - right.startedAtMs)) {
    const microLifePresentation = fishingMicroLifePresentationByEventKind[event.kind]
    if (microLifePresentation && (!event.presentation?.placement
      || validateMicroLifePlacementForKind(event.kind, event.presentation.placement) !== null)) continue
    if (!canAddFishingWorldAmbientEvent(selected, event.kind, catalog)) continue
    selected.push(event)
  }
  return { activeEvents: selected }
}
